import React, { useState } from 'react';
import Modal from '../Modal';
import { toast } from 'react-toastify';
import { sendRequest } from '../../utils/dateHelpers';
import PropTypes from 'prop-types';
import Loading from '../Loading';

function CSVUploaderGrouping({
  triggerGetData,
  buttonClassName,
  buttonLabel = 'Upload CSV For Grouping'
}) {
  const [loading, setLoading] = useState(false);
  const [, setData] = useState([]);
  const [tempData, setTempData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [columns, setColumns] = useState([]);
  const [selectedEmailColumns, setSelectedEmailColumns] = useState([]);
  const [selectedTagColumns, setSelectedTagColumns] = useState([]);
  const [selectedAuthorColumn, setSelectedAuthorColumn] = useState('');

  const handleFileUpload = event => {
    const file = event.target.files[0];
    if (!file || !file.name.endsWith('.csv')) {
      console.error('Please upload a valid CSV file.');
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      const text = reader.result;
      const lines = text.split('\n').filter(line => line.trim() !== '');
      const headers = lines[0].split(',').map(header => header.trim());

      const csvData = lines.slice(1).map(line => {
        const values = line.split(',').map(value => value.trim());
        const obj = {};
        headers.forEach((header, index) => {
          obj[header] = values[index] || '';
        });
        return obj;
      });

      setTempData(csvData);
      setColumns(headers);
    };

    reader.onerror = err => {
      console.error('Error reading file:', err);
    };

    reader.readAsText(file);
  };

  const handleClear = () => {
    setSelectedEmailColumns([]);
    setSelectedAuthorColumn('');
    setTempData([]);
    setColumns([]);
  };

  const handleUpdateClick = async (formData, filteredTagData) => {
    try {
      setLoading(true);
      const response = await sendRequest('/developers/email-bindings', 'POST', {
        emailAuthorsMappings: formData
      });

      if (response.success) {
        if (filteredTagData.length > 0) {
          await sendRequest('/developers/upload/add', 'POST', {
            emailTagMappings: filteredTagData
          });
        }

        toast.success('Successfully added!');
        triggerGetData();
        setLoading(false);
        handleClear();
      }
    } catch (error) {
      console.error('Error adding email:', error);
      setLoading(false);
    }
  };

  const handleUseTempData = () => {
    if (
      selectedEmailColumns.length === 0 ||
      !selectedAuthorColumn ||
      columns.length === 0
    ) {
      toast.error(
        'Please select at least one author column and a email column!'
      );
      return;
    }

    const filteredData = tempData.map(row => {
      const email = [];
      selectedEmailColumns.forEach((col, key) => {
        if (row[col]) {
          email[key] = row[col];
        }
      });

      return {
        emails: [...email],
        author: row[selectedAuthorColumn]
      };
    });

    const filteredTagDataArray = [];

    tempData.forEach(row => {
      selectedEmailColumns.forEach(emailCol => {
        if (row[emailCol]) {
          selectedTagColumns.forEach(tagCol => {
            if (row[tagCol]) {
              filteredTagDataArray.push({
                email: row[emailCol],
                tag: row[tagCol]
              });
            }
          });
        }
      });
    });

    if (
      filteredTagDataArray.some(
        item =>
          String(item.tag || '')
            .trim()
            .toLowerCase() === 'ai agent'
      )
    ) {
      toast.error(
        'AI Agent can only be assigned from the developer edit screen.'
      );
      return;
    }

    handleUpdateClick(filteredData, filteredTagDataArray);
    setData(filteredData);
  };

  const handleEmailColumnSelect = event => {
    const selectedOptions = Array.from(event.target.selectedOptions).map(
      option => option.value
    );
    setSelectedEmailColumns(selectedOptions);
  };

  const handleTagColumnSelect = event => {
    const selectedOptions = Array.from(event.target.selectedOptions).map(
      option => option.value
    );
    setSelectedTagColumns(selectedOptions);
  };

  const handleAuthorColumnSelect = event => {
    setSelectedAuthorColumn(event.target.value);
  };

  return (
    <>
      {modalOpen && (
        <Modal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          title="Upload CSV"
        >
          <div className="space-y-6">
            <div className="flex gap-2">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="border rounded px-3 py-2 w-full"
              />
            </div>
            {loading && <Loading type="csv" />}

            {!loading && columns.length > 0 && (
              <>
                <div className="mt-4 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label
                        htmlFor="tag-select"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Select Author Name Column:
                      </label>
                      <select
                        id="tag-select"
                        value={selectedAuthorColumn}
                        onChange={handleAuthorColumnSelect}
                        className="border rounded px-3 py-2 w-full"
                      >
                        <option value="">
                          -- Select Author Name Column --
                        </option>
                        {columns.map(column => (
                          <option key={column} value={column}>
                            {column}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label
                        htmlFor="email-select"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Select Email Column(s):
                      </label>
                      <select
                        id="email-select"
                        value={selectedEmailColumns}
                        onChange={handleEmailColumnSelect}
                        multiple={true}
                        className="border rounded px-3 py-2 w-full"
                      >
                        {columns.map(column => (
                          <option key={column} value={column}>
                            {column}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label
                        htmlFor="email-select"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Select Tags Column(s):
                      </label>
                      <select
                        id="email-select"
                        value={selectedTagColumns}
                        onChange={handleTagColumnSelect}
                        multiple={true}
                        className="border rounded px-3 py-2 w-full"
                      >
                        {columns.map(column => (
                          <option key={column} value={column}>
                            {column}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="flex justify-between mt-4">
                    <div className="flex gap-4">
                      <button
                        onClick={handleUseTempData}
                        className="bg-green-500 text-white px-6 py-2 rounded-md hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-400"
                      >
                        Use Data
                      </button>
                      <button
                        type="button"
                        onClick={handleClear}
                        className="bg-red-500 text-white px-6 py-2 rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-400"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-normal text-gray-200">Preview</h3>
                  <pre className="whitespace-pre-wrap break-words text-sm overflow-auto max-h-60 mt-4 bg-black text-white p-4 rounded-md">
                    {JSON.stringify(tempData.slice(0, 10), null, 2)}
                  </pre>
                </div>
              </>
            )}
          </div>
        </Modal>
      )}
      <button
        onClick={() => setModalOpen(true)}
        className={
          buttonClassName ||
          'mb-2 rounded bg-blue-500 px-2 py-1 text-sm text-white transition hover:bg-blue-700'
        }
        type="button"
      >
        {buttonLabel}
      </button>
    </>
  );
}

CSVUploaderGrouping.propTypes = {
  triggerGetData: PropTypes.func.isRequired,
  buttonClassName: PropTypes.string,
  buttonLabel: PropTypes.string
};

export default CSVUploaderGrouping;
